// src/lib/itemSearch.js
// #604 D7, Session 79.21c. Extracted from InventoryActionCatalog.js so it can
// be tested without mounting a page that imports supabase.

import { ORDER_IDENTIFIERS } from './orderIdentifiers';

// #604 D7, Session 79.21c -- HOW FORGIVING THE SEARCH IS. Colby's requirement,
// added while walking D5 and D6: "make sure searching can fuzzy match".
//
// This was a strict substring test: everything typed had to appear as one
// unbroken run, in that order. Against real names -- "Dibond Brushed 4x8",
// "Vinyl Gloss White 54\"", "Coroplast 4mm 4x8" -- that fails constantly.
// `dibond 4x8` found nothing because "Brushed" sits between the words, and
// `white vinyl` found nothing because the order was wrong.
//
// Layer 1 is word matching: every word must appear SOMEWHERE, in any order.
// Layer 2 is one edit of typo tolerance, two for words of eight characters or
// more -- and it runs ONLY where an exact match already failed, so it cannot
// add noise to a search that was working. That ordering is the whole reason
// fuzzy matching is safe to turn on here.
//
// ** ORDER NUMBERS STAY EXACT, AND THAT IS A CORRECTNESS RULE. ** Receive stock
// searches ORDER_IDENTIFIERS as well as names, and one edit of forgiveness
// makes 794001 match 794002 -- somebody receiving a pallet against the wrong
// order writes stock to the wrong job, with no screen showing anything amiss.
// Fuzzy on names, exact on anything that identifies a document.
//
// NOT BUILT: ranking by how well a result matches. Everything matching returns
// in the list's existing order. Said here so its absence is a decision rather
// than an oversight.
function withinOneEdit(word, target, budget) {
  // Bounded Levenshtein. Returns true when `word` is reachable from any
  // substring-anchored position of `target` within `budget` edits. Cheap
  // because budget is 1 or 2 and words are short.
  if (Math.abs(word.length - target.length) > budget) return false;
  let prev = Array.from({ length: target.length + 1 }, (_, i) => i);
  for (let i = 1; i <= word.length; i++) {
    const row = [i];
    let best = i;
    for (let j = 1; j <= target.length; j++) {
      const cost = word[i - 1] === target[j - 1] ? 0 : 1;
      row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + cost);
      if (row[j] < best) best = row[j];
    }
    if (best > budget) return false;
    prev = row;
  }
  return prev[target.length] <= budget;
}

function wordMatches(word, haystack) {
  if (haystack.includes(word)) return true;
  // Only now, and only against whole words of the name.
  if (word.length < 4) return false;
  const budget = word.length >= 8 ? 2 : 1;
  return haystack.split(/[^a-z0-9]+/i).some(t => t && withinOneEdit(word, t, budget));
}

export function matchesSearch(item, search) {
  const q = String(search || '').trim().toLowerCase();
  if (!q) return true;

  // EXACT, always, on anything that identifies a document.
  const ids = ORDER_IDENTIFIERS.map(f => item.identifiers?.[f.key]);
  if (ids.some(v => v && String(v).toLowerCase().includes(q))) return true;

  const name = String(item.name || '').toLowerCase();
  if (name.includes(q)) return true;

  const words = q.split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  return words.every(w => wordMatches(w, name));
}
